import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Store, LogOut, Shield, BarChart3, ShoppingBag, IndianRupee, Menu, Users, Database, Wallet, Lock, TrendingUp } from 'lucide-react';
import StoreManagement from './StoreManagement';
import DataExtraction from './DataExtraction';
import SuperAdminUserManagement from './SuperAdminUserManagement';
import SystemBackup from './SystemBackup';
import LoyaltyWalletControl from './LoyaltyWalletControl';
import SuperAdminSalesDashboard from './SuperAdminSalesDashboard';
import apiClient from '@/services/api';

const SuperAdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [stores, setStores] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalStores: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalMenu: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Set page title
    document.title = 'SSPICE Super Admin';
    
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.id) {
      navigate('/login');
      return;
    }
    setUser(userData);
    loadStores();
    loadAnalytics();
  }, []);

  const loadStores = async () => {
    try {
      const response = await apiClient.get('/stores');
      setStores(response.data);
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      // Get all stores to calculate stats
      const storesRes = await apiClient.get('/stores');
      const allStores = storesRes.data || [];

      let totalOrders = 0;
      let totalRevenue = 0;
      let totalMenu = 0;

      // Reuse the same revenue-accurate logic as the POS "Today's Sales"
      // dashboard (Post Pay counts immediately, prepaid only once PAID,
      // cancelled always excluded) — this used to sum GET /orders
      // directly, which is capped at 100 orders per store and counts
      // every order regardless of payment status, producing numbers wildly
      // lower than reality once a store has real order history.
      for (const store of allStores) {
        try {
          const periodRes = await apiClient.get('/analytics/dashboard/period', {
            params: { store_id: store.id, period: 'all' },
          });
          totalOrders += periodRes.data.total_orders || 0;
          totalRevenue += periodRes.data.total_revenue || 0;

          const menuRes = await apiClient.get(`/menu?store_id=${store.id}`);
          const menu = menuRes.data || [];
          totalMenu += menu.length;
        } catch (err) {
          console.error(`Error loading data for store ${store.name}:`, err);
        }
      }

      setAnalytics({
        totalStores: allStores.length,
        totalOrders,
        totalRevenue,
        totalMenu
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-gradient-to-r from-purple-700 to-indigo-700 shadow-lg border-b border-purple-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-white mr-3" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-white">SSPICE Super Admin</span>
                  <span className="text-xs text-red-400 font-medium">(Designed by Prashant B)</span>
                </div>
                <p className="text-xs text-purple-200">Master Control Panel</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-white">Welcome, {user?.name}</span>
              <Button variant="outline" size="sm" onClick={() => navigate('/change-password')} className="bg-white text-purple-700 hover:bg-purple-50">
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} className="bg-white text-purple-700 hover:bg-purple-50">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Analytics Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardHeader>
              <CardTitle className="flex items-center text-white">
                <Store className="w-5 h-5 mr-2" />
                Total Stores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold">{analytics.totalStores}</p>
              <p className="text-sm text-purple-100 mt-2">Active stores</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardHeader>
              <CardTitle className="flex items-center text-white">
                <ShoppingBag className="w-5 h-5 mr-2" />
                Total Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold">{analytics.totalOrders}</p>
              <p className="text-sm text-blue-100 mt-2">All time orders</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardHeader>
              <CardTitle className="flex items-center text-white">
                <IndianRupee className="w-5 h-5 mr-2" />
                Total Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold">₹{analytics.totalRevenue.toFixed(0)}</p>
              <p className="text-sm text-green-100 mt-2">All time revenue</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardHeader>
              <CardTitle className="flex items-center text-white">
                <Menu className="w-5 h-5 mr-2" />
                Menu Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold">{analytics.totalMenu}</p>
              <p className="text-sm text-orange-100 mt-2">Total menu items</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="stores" className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="stores">
              <Store className="w-4 h-4 mr-2" />
              Store Management
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              User Management
            </TabsTrigger>
            <TabsTrigger value="loyalty">
              <Wallet className="w-4 h-4 mr-2" />
              Loyalty &amp; Wallet
            </TabsTrigger>
            <TabsTrigger value="sales">
              <TrendingUp className="w-4 h-4 mr-2" />
              Sales
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Data & Analytics
            </TabsTrigger>
            <TabsTrigger value="backup" data-testid="backup-tab">
              <Database className="w-4 h-4 mr-2" />
              Backup & Restore
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stores">
            <StoreManagement />
          </TabsContent>

          <TabsContent value="users">
            <SuperAdminUserManagement />
          </TabsContent>

          <TabsContent value="loyalty">
            <LoyaltyWalletControl />
          </TabsContent>

          <TabsContent value="sales">
            <SuperAdminSalesDashboard />
          </TabsContent>

          <TabsContent value="analytics">
            <DataExtraction stores={stores} />
          </TabsContent>

          <TabsContent value="backup">
            <SystemBackup />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
